import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ImageIcon, X } from 'lucide-react';

export function parseGrievanceAttachments(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((u) => String(u)).filter((u) => u && u !== 'null');
}

type Props = {
  attachments: unknown;
  className?: string;
};

export const GrievanceAttachmentChips: React.FC<Props> = ({ attachments, className = '' }) => {
  const urls = parseGrievanceAttachments(attachments);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!previewUrl) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPreviewUrl(null);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [previewUrl]);

  if (urls.length === 0) return null;

  const previewModal =
    previewUrl &&
    createPortal(
      <div
        className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4"
        role="dialog"
        aria-modal="true"
        aria-label="Attachment preview"
        onClick={() => setPreviewUrl(null)}
      >
        <button
          type="button"
          onClick={() => setPreviewUrl(null)}
          className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
          aria-label="Close preview"
        >
          <X className="h-6 w-6" />
        </button>
        <img
          src={previewUrl}
          alt="Grievance attachment"
          className="max-h-[min(85vh,900px)] max-w-full rounded-lg object-contain shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        />
      </div>,
      document.body
    );

  return (
    <>
      <div className={className}>
        <p className="mb-2 text-xs font-medium text-gray-500 dark:text-gray-400">
          Attachments ({urls.length})
        </p>
        <div className="flex flex-wrap gap-2">
          {urls.map((url, index) => (
            <button
              key={`${url}-${index}`}
              type="button"
              onClick={() => setPreviewUrl(url)}
              className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition-colors hover:border-primary-400 hover:bg-primary-50 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-primary-500 dark:hover:bg-primary-950/50 dark:hover:text-primary-300 dark:focus:ring-offset-gray-900"
            >
              <ImageIcon className="h-3.5 w-3.5 shrink-0 opacity-80" />
              Image {index + 1}
            </button>
          ))}
        </div>
      </div>
      {previewModal}
    </>
  );
};
